import { useEffect, useState } from 'react';

import { DEBOUNCE_DELAY } from '@/constants';

/**
 * A custom hook to debounce a value.
 *
 * @param {unknown} value - The value to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {[unknown, Function]} - A value and a function to set the value
 */
function useDebounce<T>(value: T, delay: number = DEBOUNCE_DELAY): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
}

export default useDebounce;

/* EXAMPLE
const App = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    // Perform search API call with debouncedSearchTerm
    // ...
  }, [debouncedSearchTerm]);
  return (
    <div>
      <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
    </div>
  );
};
*/
